import React, { useEffect, useState, useCallback } from "react";
import { plyxApi } from "@/lib/plyxApi";
import { Target, Coins, Sparkles, Plus, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * StreamGoalWidget — a thin progress bar shown above the viewer's bottom
 * rail (and as a passive indicator on the broadcaster's stage).
 *
 * Props:
 *   roomName    string  — Plyx live room
 *   isBroadcaster bool  — show the "Set goal" button + edit affordances
 *   liveProgress object — optional eager-update payload from /tip response
 *
 * Behaviour:
 *   - Polls /plyx/live/{room}/goal every 8s as a fallback.
 *   - Accepts `liveProgress` prop (passed by the parent when a tip
 *     response carries an updated goal). This avoids a poll round-trip.
 *   - On completion (status === "completed") fires a one-shot celebration
 *     animation (gold sparkle burst + label morphs to "Goal hit! 🎉").
 */
const StreamGoalWidget = ({ roomName, isBroadcaster = false, liveProgress = null }) => {
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState(500);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [celebrated, setCelebrated] = useState(false);

  const fetchGoal = useCallback(async () => {
    try {
      const r = await plyxApi.get(`/plyx/live/${roomName}/goal`);
      setGoal(r.data?.goal || null);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [roomName]);

  useEffect(() => {
    fetchGoal();
    const t = setInterval(fetchGoal, 8000);
    return () => clearInterval(t);
  }, [fetchGoal]);

  // Eager update when the parent receives a tip-response payload.
  useEffect(() => {
    if (liveProgress) setGoal(liveProgress);
  }, [liveProgress]);

  // One-shot celebration when the goal first hits 100%.
  useEffect(() => {
    if (goal?.status === "completed" && !celebrated) {
      setCelebrated(true);
      toast.success(`🎉 Goal hit — ${goal.label}`);
    }
  }, [goal, celebrated]);

  const saveGoal = async () => {
    if (!label.trim() || !target) { toast.error("Add a target + a one-line label"); return; }
    setSaving(true);
    try {
      const r = await plyxApi.post(`/plyx/live/${roomName}/goal`, {
        goal_type: "tips_coins",
        target: Number(target),
        label: label.trim(),
      });
      setGoal(r.data.goal);
      setEditing(false);
      setCelebrated(false);
      toast.success("Goal set!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set goal");
    } finally { setSaving(false); }
  };

  const cancelGoal = async () => {
    if (!window.confirm("End this goal?")) return;
    try {
      await plyxApi.delete(`/plyx/live/${roomName}/goal`);
      setGoal(null);
      toast.message("Goal ended");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not end goal");
    }
  };

  // Empty state (only broadcasters get a "Set goal" button)
  if (!loading && !goal && !editing) {
    if (!isBroadcaster) return null;
    return (
      <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 pointer-events-auto">
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid="plyx-live-goal-set-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white text-xs font-bold transition"
        >
          <Target className="w-3.5 h-3.5" /> Set a goal
        </button>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 pointer-events-auto bg-black/90 border border-white/10 rounded-2xl p-4 w-80 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <p className="font-bold text-white text-sm flex items-center gap-1.5"><Target className="w-4 h-4 text-[#FFCC2E]"/> New stream goal</p>
          <button onClick={() => setEditing(false)} className="text-white/60 hover:text-white"><X className="w-4 h-4"/></button>
        </div>
        <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Target (tip coins)</label>
        <div className="flex items-center gap-1 mb-3">
          <Coins className="w-4 h-4 text-[#FFCC2E]"/>
          <input
            type="number"
            min="1"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="plyx-live-goal-target-input"
            className="flex-1 bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-white text-sm"
          />
        </div>
        <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">What unlocks at the target?</label>
        <input
          type="text"
          maxLength={80}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Q&A round, song request, dance break"
          data-testid="plyx-live-goal-label-input"
          className="w-full bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-white text-sm mb-3"
        />
        <button
          type="button"
          onClick={saveGoal}
          disabled={saving || !label.trim()}
          data-testid="plyx-live-goal-save-btn"
          className="w-full py-2 rounded-md bg-gradient-to-r from-[#FFCC2E] to-[#FF2D7A] text-black font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-1"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Check className="w-4 h-4"/>}
          Set goal
        </button>
      </div>
    );
  }

  if (!goal) return null;

  const isComplete = goal.status === "completed";
  return (
    <div
      data-testid="plyx-live-goal-widget"
      data-percent={goal.percent}
      data-status={goal.status}
      className="absolute top-16 left-1/2 -translate-x-1/2 z-20 w-[min(92vw,560px)] pointer-events-auto"
    >
      <div className={`rounded-2xl border backdrop-blur p-3 ${isComplete ? "border-[#FFD700] bg-[#FFD700]/10 animate-pulse" : "border-white/15 bg-black/60"}`}>
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            {isComplete ? <Sparkles className="w-4 h-4 text-[#FFD700]"/> : <Target className="w-4 h-4 text-[#FFCC2E]"/>}
            <p className="text-white text-xs sm:text-sm font-bold truncate">
              {isComplete ? `🎉 ${goal.label}` : goal.label}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-white/90 text-xs font-mono tabular-nums">
              {goal.current.toLocaleString()} / {goal.target.toLocaleString()}
            </span>
            {isBroadcaster && (
              <button
                onClick={cancelGoal}
                title="End goal"
                aria-label="End goal"
                data-testid="plyx-live-goal-cancel-btn"
                className="w-5 h-5 rounded-full bg-white/10 hover:bg-red-600/30 text-white/60 hover:text-red-200 flex items-center justify-center"
              >
                <X className="w-3 h-3"/>
              </button>
            )}
          </div>
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${isComplete ? "bg-gradient-to-r from-[#FFD700] to-[#FFB42D]" : "bg-gradient-to-r from-[#FF2D7A] via-[#FFCC2E] to-[#00E0C7]"}`}
            style={{ width: `${goal.percent}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default StreamGoalWidget;
